/**
 * Driver Dashboard - Post-Approval Dashboard for PPV Drivers
 * Shows pending application status OR full driver features if approved
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { formatAddress } from '../lib/formatAddress';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import api from '../lib/api';
import PWAInstallPrompt from '../components/PWAInstallPrompt';
import { 
  Link2, ArrowLeft, Power, Truck, Package, MapPin, DollarSign, 
  CheckCircle, Clock, QrCode, ChevronRight, AlertCircle, XCircle,
  Calendar, Star, TrendingUp, Bell, Settings, FileText, User, Camera
} from 'lucide-react';

const DriverDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [driver, setDriver] = useState(null);
  const [application, setApplication] = useState(null);
  const [jobs, setJobs] = useState({ assigned: [], available: [] });
  const [earnings, setEarnings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusLoading, setStatusLoading] = useState(false);
  const [urgentOffers, setUrgentOffers] = useState([]);
  const [acceptingOffer, setAcceptingOffer] = useState(null);
  const [urgentCountdowns, setUrgentCountdowns] = useState({});
  const prevOfferCountRef = useRef(0);

  // Alert sound using Web Audio API
  const playUrgentAlert = useCallback(() => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const playTone = (freq, startTime, duration) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = freq;
        osc.type = 'square';
        gain.gain.setValueAtTime(0.15, startTime);
        gain.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
        osc.start(startTime);
        osc.stop(startTime + duration);
      };
      // 3-tone urgent alert
      playTone(880, ctx.currentTime, 0.15);
      playTone(1100, ctx.currentTime + 0.18, 0.15);
      playTone(1320, ctx.currentTime + 0.36, 0.25);
    } catch {}
  }, []);

  useEffect(() => {
    fetchDriverData();
  }, []);

  // Poll for urgent offers every 15 seconds
  useEffect(() => {
    if (!application || application.status !== 'approved') return;
    const fetchUrgent = async () => {
      try {
        const res = await api.get('/urgent-offers/available');
        if (res?.data?.offers) {
          const newOffers = res.data.offers;
          // Play alert if new offers appeared
          if (newOffers.length > prevOfferCountRef.current && prevOfferCountRef.current >= 0) {
            playUrgentAlert();
          }
          prevOfferCountRef.current = newOffers.length;
          setUrgentOffers(newOffers);
        }
      } catch {}
    };
    fetchUrgent();
    const interval = setInterval(fetchUrgent, 15000);
    return () => clearInterval(interval);
  }, [application, playUrgentAlert]);

  const handleAcceptOffer = async (offerId) => {
    setAcceptingOffer(offerId);
    try {
      const res = await api.post(`/urgent-offers/${offerId}/accept`);
      if (res.data.success) {
        toast.success(res.data.message);
        setUrgentOffers(prev => prev.filter(o => o.id !== offerId));
        fetchDriverData();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to accept offer');
    } finally {
      setAcceptingOffer(null);
    }
  };

  const fetchDriverData = async () => {
    setLoading(true);
    try {
      // First check application status
      const appRes = await api.get('/ppv/my-application').catch(() => null);
      if (appRes?.data) setApplication(appRes.data);

      // If approved, fetch driver data
      if (appRes?.data?.status === 'approved') {
        const [driverRes, jobsRes, earningsRes] = await Promise.all([
          api.get('/drivers/me').catch(() => null),
          api.get('/drivers/jobs').catch(() => ({ data: { assigned: [], available: [] } })),
          api.get('/drivers/earnings').catch(() => ({ data: {} }))
        ]);
        if (driverRes?.data) setDriver(driverRes.data);
        if (jobsRes?.data) setJobs(jobsRes.data);
        if (earningsRes?.data) setEarnings(earningsRes.data);
      }
    } catch (error) {
      console.error('Failed to load driver data:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleOnline = async () => {
    if (!driver) return;
    setStatusLoading(true);
    try {
      const newStatus = driver.is_online ? 'offline' : 'online';
      await api.post('/drivers/status', { status: newStatus });
      setDriver(prev => ({ ...prev, is_online: !prev.is_online }));
      toast.success(`You are now ${newStatus === 'online' ? 'Online' : 'Offline'}`);
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setStatusLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  // APPLICATION STATUS VIEW (Pending/Rejected)
  if (!application || application.status !== 'approved') {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button onClick={() => navigate('/')} variant="ghost" size="sm"><ArrowLeft className="h-4 w-4" /></Button>
              <div className="flex items-center gap-3">
                <Link2 className="h-7 w-7 text-primary" />
                <span className="text-xl font-bold">Liantiao</span>
                <Badge variant="outline" className="text-green-500 border-green-500/30">DRIVER</Badge>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={logout}>Sign Out</Button>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-6 py-12">
          {!application ? (
            <Card className="text-center p-12">
              <FileText className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
              <h2 className="text-2xl font-bold mb-2">No Application Found</h2>
              <p className="text-muted-foreground mb-6">You haven't submitted a driver application yet.</p>
              <Button variant="jamaica" onClick={() => navigate('/driver/apply')}>Apply Now</Button>
            </Card>
          ) : application.status === 'pending' ? (
            <Card>
              <CardHeader className="text-center">
                <div className="w-20 h-20 rounded-full bg-yellow-500/10 flex items-center justify-center mx-auto mb-4">
                  <Clock className="h-10 w-10 text-yellow-500" />
                </div>
                <CardTitle className="text-2xl">Application Under Review</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="text-center text-muted-foreground">
                  <p>Thank you for applying to become a Liantiao PPV Driver!</p>
                  <p>Our team is reviewing your application. You'll be notified once a decision is made.</p>
                </div>
                
                <div className="bg-muted/30 rounded-lg p-6 space-y-4">
                  <h3 className="font-semibold flex items-center gap-2"><User className="h-5 w-5" />Application Details</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div><span className="text-muted-foreground">Name:</span> <span className="font-medium">{application.full_name}</span></div>
                    <div><span className="text-muted-foreground">Phone:</span> <span className="font-medium">{application.phone}</span></div>
                    <div><span className="text-muted-foreground">Vehicle:</span> <span className="font-medium">{application.vehicle_make} {application.vehicle_model}</span></div>
                    <div><span className="text-muted-foreground">Year:</span> <span className="font-medium">{application.vehicle_year}</span></div>
                    <div><span className="text-muted-foreground">License Plate:</span> <span className="font-medium">{application.license_plate}</span></div>
                    <div><span className="text-muted-foreground">Submitted:</span> <span className="font-medium">{new Date(application.created_at).toLocaleDateString()}</span></div>
                  </div>
                </div>

                <div className="text-center">
                  <p className="text-sm text-muted-foreground mb-4">Estimated review time: 1-3 business days</p>
                  <Button variant="outline" onClick={() => navigate('/my-applications')}>View All Applications</Button>
                </div>
              </CardContent>
            </Card>
          ) : application.status === 'rejected' ? (
            <Card>
              <CardHeader className="text-center">
                <div className="w-20 h-20 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
                  <XCircle className="h-10 w-10 text-destructive" />
                </div>
                <CardTitle className="text-2xl">Application Not Approved</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="text-center text-muted-foreground">
                  <p>Unfortunately, your driver application was not approved at this time.</p>
                  {application.rejection_reason && (
                    <div className="mt-4 p-4 bg-destructive/5 border border-destructive/20 rounded-lg text-left">
                      <p className="font-medium text-destructive mb-1">Reason:</p>
                      <p className="text-foreground">{application.rejection_reason}</p>
                    </div>
                  )}
                </div>
                <div className="text-center space-y-3">
                  <p className="text-sm text-muted-foreground">You may reapply after addressing the feedback above.</p>
                  <div className="flex gap-3 justify-center">
                    <Button variant="outline" onClick={() => navigate('/')}>Go Home</Button>
                    <Button variant="jamaica" onClick={() => navigate('/driver/apply')}>Apply Again</Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : null}
        </main>
      </div>
    );
  }

  // APPROVED DRIVER DASHBOARD
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button onClick={() => navigate('/')} variant="ghost" size="sm"><ArrowLeft className="h-4 w-4" /></Button>
            <div className="flex items-center gap-3">
              <Link2 className="h-7 w-7 text-primary" />
              <span className="text-xl font-bold">Liantiao</span>
              <Badge className="bg-green-500 text-white">DRIVER</Badge>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon"><Bell className="h-5 w-5" /></Button>
            <Button variant="ghost" size="icon" onClick={() => navigate('/driver/settings')}><Settings className="h-5 w-5" /></Button>
            <Button 
              variant={driver?.is_online ? "destructive" : "jamaica"} 
              onClick={toggleOnline} 
              disabled={statusLoading}
              className="gap-2"
            >
              <Power className="h-4 w-4" />
              {driver?.is_online ? 'Go Offline' : 'Go Online'}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Welcome Banner */}
        <div className="mb-8 p-6 rounded-2xl bg-gradient-to-r from-green-600 to-green-500 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold mb-1">Welcome back, {driver?.name || user?.name || 'Driver'}!</h1>
              <p className="text-green-100">
                {driver?.is_online ? "You're online and ready to accept deliveries" : "Go online to start earning"}
              </p>
            </div>
            <div className="text-right">
              <div className="flex items-center gap-2 mb-1">
                <Star className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                <span className="text-2xl font-bold">{driver?.rating || '5.0'}</span>
              </div>
              <p className="text-green-100 text-sm">{driver?.total_deliveries || 0} deliveries</p>
            </div>
          </div>
        </div>

        {/* URGENT OFFERS BANNER */}
        {urgentOffers.length > 0 && (
          <div className="mb-6 space-y-3" data-testid="urgent-offers-section">
            {urgentOffers.map(offer => (
              <div 
                key={offer.id}
                className={`relative overflow-hidden rounded-2xl border-2 ${
                  offer.is_hot 
                    ? 'border-orange-500 bg-gradient-to-r from-orange-600/20 via-red-600/20 to-orange-600/20 animate-pulse' 
                    : 'border-red-500 bg-gradient-to-r from-red-600/20 via-red-500/10 to-red-600/20 animate-pulse'
                }`}
                data-testid={`urgent-offer-${offer.id}`}
              >
                <div className={`absolute inset-0 ${offer.is_hot ? 'bg-orange-500/5' : 'bg-red-500/5'}`} />
                <div className="relative p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className={`${offer.is_hot ? 'bg-orange-500 text-white' : 'bg-red-500 text-white'} font-bold text-xs px-3 py-1`} data-testid="urgent-badge">
                          HIGH PRIORITY
                        </Badge>
                        <span className={`font-black text-lg ${offer.is_hot ? 'text-orange-400' : 'text-red-400'}`} data-testid="urgent-offer-label">
                          {offer.label}
                        </span>
                      </div>
                      <div className="space-y-1 text-sm">
                        <div className="flex items-center gap-2 text-slate-300">
                          <MapPin className="h-4 w-4 text-green-400 flex-shrink-0" />
                          <span>{offer.pickup_address?.street || offer.pickup_address?.parish || 'Pickup'}</span>
                          <span className="text-slate-500">-&gt;</span>
                          <span>{offer.dropoff_address?.street || offer.dropoff_address?.parish || 'Dropoff'}</span>
                        </div>
                        <div className="flex items-center gap-4 text-slate-400">
                          <span>Size: {offer.package_size}</span>
                          <span>From: {offer.sender_name}</span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right flex flex-col items-end gap-2">
                      <div className={`text-3xl font-black ${offer.is_hot ? 'text-orange-400' : 'text-red-400'}`} data-testid="urgent-offer-amount">
                        J${(offer.driver_earnings || Math.round((offer.offer_amount || 0) * 0.65)).toLocaleString()}
                      </div>
                      <span className="text-xs text-slate-400">Your earnings</span>
                      <Button
                        onClick={() => handleAcceptOffer(offer.id)}
                        disabled={acceptingOffer === offer.id}
                        className={`${offer.is_hot ? 'bg-orange-500 hover:bg-orange-600' : 'bg-red-500 hover:bg-red-600'} text-white font-bold px-6`}
                        data-testid={`accept-offer-${offer.id}`}
                      >
                        {acceptingOffer === offer.id ? (
                          <><Clock className="h-4 w-4 mr-1 animate-spin" /> Accepting...</>
                        ) : (
                          <><Truck className="h-4 w-4 mr-1" /> Accept Offer</>
                        )}
                      </Button>
                      <span className="text-xs text-slate-500">25 min pickup window</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                  <DollarSign className="h-5 w-5 text-green-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Today's Earnings</p>
                  <p className="text-xl font-bold">J${earnings?.today || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <Package className="h-5 w-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Deliveries Today</p>
                  <p className="text-xl font-bold">{earnings?.deliveries_today || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-yellow-500/10 flex items-center justify-center">
                  <TrendingUp className="h-5 w-5 text-yellow-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">This Week</p>
                  <p className="text-xl font-bold">J${earnings?.this_week || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                  <Calendar className="h-5 w-5 text-purple-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">This Month</p>
                  <p className="text-xl font-bold">J${earnings?.this_month || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Assigned Jobs */}
          <div className="lg:col-span-2 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Truck className="h-5 w-5 text-primary" />
                Your Deliveries
              </h2>
              <Badge variant="outline">{jobs.assigned?.length || 0} active</Badge>
            </div>
            
            {jobs.assigned?.length === 0 ? (
              <Card className="p-8 text-center">
                <Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No active deliveries</p>
                <p className="text-sm text-muted-foreground mt-1">New jobs will appear here when assigned</p>
              </Card>
            ) : (
              jobs.assigned.map((job, i) => (
                <Card key={job.id || i} className="hover:border-primary/30 transition-colors cursor-pointer" onClick={() => navigate(`/driver/job/${job.id}`)}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Badge className={job.status === 'pickup' ? 'bg-yellow-500' : 'bg-blue-500'}>{job.status}</Badge>
                          <span className="font-mono text-sm">{job.tracking_id}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <MapPin className="h-4 w-4" />
                          <span>{formatAddress(job.pickup_address)}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          <span>{job.delivery_address}</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-green-500">J${job.payout || 0}</p>
                        <p className="text-xs text-muted-foreground">{job.distance} km</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>

          {/* Quick Actions */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold">Quick Actions</h2>
            <div className="space-y-2">
              <Button variant="outline" className="w-full justify-between" onClick={() => navigate('/driver/scan')}>
                <span className="flex items-center gap-2"><QrCode className="h-4 w-4" />Scan Package</span>
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button variant="outline" className="w-full justify-between" onClick={() => navigate('/driver/earnings')}>
                <span className="flex items-center gap-2"><DollarSign className="h-4 w-4" />View Earnings</span>
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button variant="outline" className="w-full justify-between" onClick={() => navigate('/driver/history')}>
                <span className="flex items-center gap-2"><Clock className="h-4 w-4" />Delivery History</span>
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button variant="outline" className="w-full justify-between" onClick={() => navigate('/driver/vehicle-photos')}>
                <span className="flex items-center gap-2"><Camera className="h-4 w-4" />Vehicle Photos</span>
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button variant="outline" className="w-full justify-between" onClick={() => navigate('/support')}>
                <span className="flex items-center gap-2"><AlertCircle className="h-4 w-4" />Get Support</span>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>

            {/* Install App Card */}
            <PWAInstallPrompt variant="card" />

            {/* Available Jobs Preview */}
            {jobs.available?.length > 0 && (
              <Card className="mt-6">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center justify-between">
                    <span>Available Nearby</span>
                    <Badge variant="secondary">{jobs.available.length}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {jobs.available.slice(0, 3).map((job, i) => (
                    <div key={job.id || i} className="flex items-center justify-between p-2 rounded-lg bg-muted/50">
                      <div className="text-sm">
                        <p className="font-medium">{job.pickup_area}</p>
                        <p className="text-xs text-muted-foreground">{job.distance} km away</p>
                      </div>
                      <span className="font-bold text-green-500 text-sm">J${job.payout}</span>
                    </div>
                  ))}
                  <Button variant="ghost" size="sm" className="w-full mt-2" onClick={() => navigate('/driver/jobs')}>
                    View All Jobs <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default DriverDashboard;
